import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { ChevronRight, Filter, Heart, ExternalLink, Info } from 'lucide-react';

const MatchReasonBadge = ({ reasons }) => {
  if (!reasons || reasons.length === 0) return null;

  return (
    <div className="mt-2">
      <div className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide text-black/40 mb-1.5">
        <Info size={10} />
        <span>Why this match</span>
      </div>
      <div className="space-y-1">
        {reasons.map((r, i) => (
          <div key={i} className="flex items-start gap-1.5">
            <span className="mt-[3px] w-1 h-1 rounded-full bg-black/30 flex-shrink-0" />
            <span className="text-[10px] text-black/50 leading-tight">{r}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AlternativesScreenCanva = () => {
  const { setCurrentScreen, setActiveTab, addToWishlist } = useApp();
  const [showFilters, setShowFilters] = useState(false);
  const [product, setProduct] = useState(null);
  const [alternatives, setAlternatives] = useState([]);
  const [cameFrom, setCameFrom] = useState('results-canva');

  useEffect(() => {
    const savedResult = sessionStorage.getItem('currentScanResult');
    if (savedResult) {
      try {
        const data = JSON.parse(savedResult);
        setProduct(data);
        
        const backendAlternatives = data.alternatives || [];
        
        const transformedAlternatives = backendAlternatives.map((alt, idx) => ({
          id: alt.id || idx,
          name: alt.name,
          brand: alt.brand,
          price: alt.price,
          fabricScore: Math.round((alt.fabric_score || 0) * 20),
          sustainabilityScore: Math.round((alt.sustainability_score || 0) * 20),
          image: alt.image_url || 'https://images.unsplash.com/photo-1620799140188-3b2a02fd9a77?w=300&h=400&fit=crop',
          url: alt.product_url || '#',
          reason: alt.reason || '',
          tags: alt.tags || [],
          matchReasons: alt.match_reasons || [],
        }));

        setAlternatives(transformedAlternatives);
      } catch (e) {
        console.error('Error loading alternatives:', e);
      }
    }
  }, []);

  if (!product) {
    return (
      <div className="min-h-screen bg-white pb-24 flex items-center justify-center">
        <p className="text-black/55" data-testid="alternatives-loading">Loading alternatives...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24" data-testid="alternatives-screen">
      <div className="px-6 pt-6 pb-4 flex items-center justify-between border-b border-black/10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setCurrentScreen(cameFrom)}
            className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center hover:bg-black/10 transition-all flex-shrink-0"
            data-testid="alternatives-back-btn"
          >
            <ChevronRight size={18} className="text-black rotate-180" />
          </button>
          <button onClick={() => setActiveTab("home")} className="hover:opacity-80 transition-opacity"><img src="/logos/logo-black.jpg" alt="ATELIA" className="h-16 w-auto object-contain" /></button>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center hover:bg-black/10"
          data-testid="alternatives-filter-btn"
        >
          <Filter size={18} className="text-black" />
        </button>
      </div>

      <div className="px-6 pt-6 pb-4">
        <p className="text-xs uppercase tracking-wide text-black/55 mb-2 font-bold">SMART ALTERNATIVES</p>
        <h1 className="text-2xl font-bold text-black uppercase tracking-wide">Better Options</h1>
        <p className="text-sm text-black/55 mt-1">
          {alternatives.length > 0 ? `${alternatives.length} alternatives found` : 'Finding alternatives...'}
        </p>
      </div>

      {showFilters && (
        <div className="px-6 mb-4">
          <div className="bg-black/5 rounded-xl p-4 border border-black/10 space-y-3">
            <div>
              <p className="text-xs uppercase font-bold text-black/55 mb-2">Categories</p>
              <div className="flex gap-2 flex-wrap">
                {['Sweaters', 'Shirts', 'Knitwear'].map(cat => (
                  <button key={cat} className="px-3 py-1.5 bg-white border border-black/12 rounded-lg text-xs font-bold uppercase">
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase font-bold text-black/55 mb-2">Fabric Type</p>
              <div className="flex gap-2 flex-wrap">
                {['Cotton', 'Wool', 'Linen'].map(fab => (
                  <button key={fab} className="px-3 py-1.5 bg-white border border-black/12 rounded-lg text-xs font-bold uppercase">
                    {fab}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-6">
        {alternatives.length === 0 ? (
          <div className="text-center py-12" data-testid="no-alternatives">
            <p className="text-black/55 text-sm mb-2">No alternatives available yet</p>
            <p className="text-black/30 text-xs">Backend is generating personalized recommendations...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {alternatives.map(item => (
              <div key={item.id} className="bg-white border border-black/12 rounded-2xl overflow-hidden" data-testid={`alternative-card-${item.id}`}>
                <div className="aspect-[3/4] relative">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  <div className="absolute top-2 left-2 space-y-1">
                    <div className="bg-white/95 backdrop-blur px-2 py-1 rounded-lg">
                      <p className="text-[10px] font-bold text-black">F: {item.fabricScore}</p>
                    </div>
                    <div className="bg-white/95 backdrop-blur px-2 py-1 rounded-lg">
                      <p className="text-[10px] font-bold text-black">S: {item.sustainabilityScore}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => addToWishlist(item)}
                    className="absolute top-2 right-2 w-8 h-8 bg-white/95 backdrop-blur rounded-full flex items-center justify-center"
                    data-testid={`alternative-wishlist-${item.id}`}
                  >
                    <Heart size={16} className="text-black" />
                  </button>
                </div>
                <div className="p-3">
                  <p className="text-[10px] text-black/55 uppercase font-bold">{item.brand}</p>
                  <p className="text-sm font-bold text-black mt-0.5 line-clamp-2">{item.name}</p>
                  {item.reason && (
                    <p className="text-[10px] text-black/45 mt-1 line-clamp-2">{item.reason}</p>
                  )}
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex gap-1 flex-wrap mt-1.5">
                      {item.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="px-1.5 py-0.5 bg-black/5 rounded text-[9px] uppercase font-bold text-black/50">{tag}</span>
                      ))}
                    </div>
                  )}
                  <MatchReasonBadge reasons={item.matchReasons} />
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 flex items-center gap-1 text-xs text-black/55 hover:text-black"
                    data-testid={`alternative-visit-${item.id}`}
                  >
                    <ExternalLink size={12} />
                    <span className="uppercase font-bold">Visit Site</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-6 mt-6">
        <button
          onClick={() => setCurrentScreen('home')}
          className="w-full bg-white border border-black/12 text-black py-3 rounded-xl text-sm font-bold uppercase"
          data-testid="alternatives-home-btn"
        >
          Back to Home
        </button>
      </div>
    </div>
  );
};

export default AlternativesScreenCanva;
